import { useAnalytics } from 'app/context/Analytics';
import useNamespacedStorage from 'app/hooks/storage/useNamespacedStorage';
import useHistory from 'app/modules/tailor/hooks/useHistory';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useIndicationSelector from 'app/hooks/useIndicationSelector';
import { useTailorHalfOpen } from 'app/hooks/useTailorHalfOpen';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';
import { cx } from '@twind/core';
import useWebchatVersion from 'app/hooks/useWebchatVersion';
import { ChatVersion } from 'app/copied/constants/common';
import { useMemo, useRef } from 'react';

import BackButton from '../../components/Header/BackButton';
import { PosthogEventName } from '../../copied/constants/PosthogEventName';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { PrimaryIndicationSelector } from '../IndicationSelector';
import CloseButton from './CloseButton';
import ExpandButton from './ExpandButton';
import SearchButton from './SearchButton';
import { Heading1 } from '../Typography';

export enum BackButtonAction {
  BACK = 'back_button',
  HOME = 'home_button',
}

export default function Header({
  onClickBack,
  backButtonAction = BackButtonAction.BACK,
  showBackButton = true,
  showCloseButton = true,
  showSearchButton = false,
  showExpandButton = false,
  showIndicationSelector = true,
  headerTitle,
  headerIcon,
  headerTextEnabled = false,
  className,
  useCustomIndicationSelectorButton = false,
}: {
  onClickBack?: () => void;
  backButtonAction?: BackButtonAction;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  showSearchButton?: boolean;
  showExpandButton?: boolean;
  showIndicationSelector?: boolean;
  headerTitle?: string;
  headerIcon?: string;
  headerTextEnabled?: boolean;
  className?: string;
  useCustomIndicationSelectorButton?: boolean;
}) {
  const webchatVersion = useWebchatVersion();
  const { t } = useTranslation();
  const { uiRefreshPreLaunchUiUpdates } = useFeatureFlags();
  const navigate = useNavigate();
  const { location } = useHistory();
  const currentPathname = location?.pathname;
  const { captureEventWithActiveScreen } = useAnalytics();
  const [isManuallyClosed] = useNamespacedStorage('ostroManuallyClosed', false);
  const { halfOpen } = useTailorHalfOpen();
  const { setIsOpen } = useTailorIsOpen();
  const backButtonLabel = t(uiRefreshPreLaunchUiUpdates ? backButtonAction : BackButtonAction.BACK);
  const { primaryIndicationSelectorEnabled } = useIndicationSelector();
  const headerContainerRef = useRef<HTMLDivElement>(null);

  const handleExpand = () => {
    captureEventWithActiveScreen(PosthogEventName.Expanded, {
      halfautoOpen: halfOpen,
    });
    setIsOpen(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    captureEventWithActiveScreen(PosthogEventName.Minimized, {
      halfautoOpen: halfOpen,
    });
    setIsOpen(false);
  };

  const handleBack = () => {
    const options = { state: { isBack: true } };

    const hasSetBackAction = uiRefreshPreLaunchUiUpdates && backButtonAction === BackButtonAction.HOME;
    const hasLocationStateFrom = Boolean(location?.state?.from);

    const navigateToTailor = () =>
      navigate('/tailor', {
        ...options,
        state: {
          ...options.state,
          nextPath: '/tailor',
          prevPath: currentPathname ?? '/tailor',
        },
      });
    const navigateToPrevious = () => navigate(-1);
    const navigateToLocationState = () => {
      return location?.state?.from ? navigate(location.state.from, options) : navigateToPrevious();
    };

    const handleNavigation = () => {
      if (hasSetBackAction) {
        navigateToTailor();
      } else if (onClickBack) {
        onClickBack();
      } else if (hasLocationStateFrom) {
        navigateToLocationState();
      } else {
        navigateToPrevious();
      }
    };

    handleNavigation();

    captureEventWithActiveScreen(PosthogEventName.BackButtonClicked, {
      autoOpen: !isManuallyClosed,
    });
  };

  const handleSearch = () => {
    navigate('/tailor/search');
  };

  const justify = 'justify-between';
  const marginBottom = showIndicationSelector && primaryIndicationSelectorEnabled ? 'mb-7' : 'mb-0';
  const indicationSelectorClassName = useCustomIndicationSelectorButton ? 'w-full' : 'max-w-[200px] min-w-0';
  const leftContainerClassName = useMemo(() => {
    if (showIndicationSelector && primaryIndicationSelectorEnabled) {
      return !useCustomIndicationSelectorButton ? 'w-[62px]' : 'flex-shrink';
    }
    return 'flex-grow';
  }, [showIndicationSelector, primaryIndicationSelectorEnabled, useCustomIndicationSelectorButton]);

  return (
    <header
      ref={headerContainerRef}
      id="ostro-tailor-header"
      className={cx(`~(w-full items-center flex text-center ${justify} ${marginBottom})`, className)}
    >
      <div className={`${leftContainerClassName} flex-shrink-0`}>
        {(headerIcon || headerTitle) && headerTextEnabled && (
          <div className="flex items-center gap-2">
            {headerIcon && (
              <img
                id="ostro-tailor-header-icon"
                src={headerIcon}
                alt="Header Icon"
                className="w-6 h-6"
              />
            )}
            {headerTitle && <Heading1 id="ostro-tailor-header-title">{headerTitle}</Heading1>}
          </div>
        )}
        {showBackButton && (
          <BackButton
            label={webchatVersion === ChatVersion.v4_1 ? '' : backButtonLabel}
            onClick={handleBack}
          />
        )}
      </div>

      {showIndicationSelector && primaryIndicationSelectorEnabled && (
        <div className="flex justify-center flex-grow">
          <PrimaryIndicationSelector
            containerRef={headerContainerRef}
            className={indicationSelectorClassName}
            useCustomButton={useCustomIndicationSelectorButton}
          />
        </div>
      )}

      <div className="flex flex-shrink-0 h-[44px] items-center justify-end w-[62px] gap-3">
        {showExpandButton && <ExpandButton onClick={handleExpand} />}
        {showSearchButton && <SearchButton onClick={handleSearch} />}
        {showCloseButton && <CloseButton onClick={handleClose} />}
      </div>
    </header>
  );
}
